/*
 * Copyright 2026 North Carolina State University
 */

import React, { useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { formatDateTime } from '../../../utils/timeFormat';

/**
 * What changed on a request form, by whom, when.
 *
 * This replaced the revision cycle. Rather than an admin sending a request
 * back and the client formally resubmitting it, either side edits the form
 * while the request is open and each save lands here as a field-level diff.
 *
 * Older requests also carry `revisions[]` from the retired cycle; the
 * migration converts those into the same change entries, so this one list
 * covers a request's whole editing history.
 */

const personName = (person, fallback) => {
  if (!person || typeof person === 'string') return fallback || '—';
  const name = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  return name || person.email || fallback || '—';
};

const ROLE_LABEL = {
  admin: 'Admin',
  client: 'Client'
};

const emptyDash = (v) => (v === '' || v === undefined || v === null ? '—' : v);

const RequestChangeLog = ({ request, dense = false }) => {
  const entries = useMemo(() => {
    const list = (request?.form_changes || []).map((c, i) => ({
      key: c._id || `change-${i}`,
      at: c.changed_at,
      who: personName(c.changed_by, c.changed_by_name),
      role: c.changed_by_role,
      comment: c.comment,
      changes: c.changes || []
    }));
    return list.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [request]);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        The form has not been changed since it was submitted.
      </Typography>
    );
  }

  return (
    <Stack spacing={dense ? 1.5 : 2}>
      {entries.map((e) => (
        <Box key={e.key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
            <Chip
              size="small"
              variant="outlined"
              label={ROLE_LABEL[e.role] || 'Edit'}
              color={e.role === 'admin' ? 'default' : 'primary'}
            />
            <Typography variant="caption" color="text.secondary">
              {e.who} · {formatDateTime(e.at)} ·{' '}
              {e.changes.length} field{e.changes.length === 1 ? '' : 's'}
            </Typography>
          </Stack>

          {e.comment && (
            <Typography variant="body2" sx={{ mb: 0.75, whiteSpace: 'pre-wrap' }}>
              {e.comment}
            </Typography>
          )}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>Field</TableCell>
                <TableCell>Was</TableCell>
                <TableCell>Now</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {e.changes.map((c, i) => (
                <TableRow key={`${e.key}-${c.field || i}`}>
                  <TableCell>{c.label || c.field}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', textDecoration: c.from ? 'line-through' : 'none' }}>
                    {emptyDash(c.from)}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{emptyDash(c.to)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Stack>
  );
};

export default RequestChangeLog;
